'use client';

import { forwardRef } from 'react';
import type { ShowDetailRecord } from '../../_lib/detail/show-detail-types';
import { LtdCalendarWidget } from './LtdCalendarWidget';

interface TicketBookingPanelProps {
  show: ShowDetailRecord;
  /** ISO date string the calendar should open on — see
   *  `LtdCalendarWidget.tsx`'s `defaultDate` doc comment. */
  defaultDate?: string;
  onDateSelect?: (dateIso: string) => void;
  initialQuantity?: number;
  onQuantityChange?: (quantity: number) => void;
  /** Show the "Tickets − n +" stepper even before the vendor has rendered its
   *  calendar table — set while the mobile calendar sheet is open. See
   *  `LtdCalendarWidget`'s `forceTicketHeader`. */
  forceTicketHeader?: boolean;
}

/**
 * Ticket Booking Panel — the whole right-hand sticky card. Date/performance
 * selection, ticket quantity, and the "Buy Tickets" hop to LTD's own booking
 * flow are now entirely owned by the real `LTD.Calendar` vendor widget (see
 * `LtdCalendarWidget.tsx`'s doc comment for the sandbox findings behind this
 * — the from-scratch calendar/showtime-list/quantity-stepper reproduction
 * this replaced is still used, unchanged, on the Seat Selection Page).
 */
export const TicketBookingPanel = forwardRef<HTMLDivElement, TicketBookingPanelProps>(function TicketBookingPanel(
  { show, defaultDate, onDateSelect, initialQuantity, onQuantityChange, forceTicketHeader },
  ref,
) {
  const { event, performances } = show;

  // Real-data-driven upper bound for the ticket-quantity stepper — see
  // `LtdCalendarWidget`'s `maxTicket` doc comment for how this differs from
  // the CMS's own per-selected-date recomputation.
  const maxTicket = Math.max(1, ...performances.map((p) => p.availableTickets || 0));

  return (
    <div className="shows-detail-booking-panel" id="ticket-booking-panel" ref={ref}>
      <LtdCalendarWidget
        eventUrlId={event.ltdEventUrlId}
        internalShowId={show.showId}
        maxTicket={maxTicket}
        defaultDate={defaultDate}
        onDateSelect={onDateSelect}
        initialQuantity={initialQuantity}
        onQuantityChange={onQuantityChange}
        forceTicketHeader={forceTicketHeader}
      />
    </div>
  );
});
